import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogShell, DialogShellBody, DialogShellHeader, Skeleton } from '@bliks-io/ui';
import type { PopulatedEntitySuggestion } from '@bliks-io/contracts';
import { useEntitySuggestion } from '@/api/queries/useEntitySuggestion';
import { readDialogLoadState } from '@/lib/dialogLoadState';
import { DialogLoadStateBody } from '@/components/dialogs/DialogLoadStateBody';
import { SuggestionReviewBody } from './review-modal/SuggestionReviewBody';
import { RejectConfirmDialog } from './review-modal/RejectConfirmDialog';

interface EntitySuggestionReviewModalProps {
  suggestionId: string | null;
  listSuggestion: PopulatedEntitySuggestion | undefined;
  onClose: () => void;
  onPreviewRecording: (recordingId: string) => void;
}

export function EntitySuggestionReviewModal({
  suggestionId,
  listSuggestion,
  onClose,
  onPreviewRecording
}: EntitySuggestionReviewModalProps) {
  if (!suggestionId) return null;
  return (
    <EntitySuggestionReviewModalInner
      key={suggestionId}
      suggestionId={suggestionId}
      listSuggestion={listSuggestion}
      onClose={onClose}
      onPreviewRecording={onPreviewRecording}
    />
  );
}

function EntitySuggestionReviewModalInner({
  suggestionId,
  listSuggestion,
  onClose,
  onPreviewRecording
}: {
  suggestionId: string;
  listSuggestion: PopulatedEntitySuggestion | undefined;
  onClose: () => void;
  onPreviewRecording: (recordingId: string) => void;
}) {
  const { t } = useTranslation();
  const [rejectOpen, setRejectOpen] = useState(false);
  const suggestionQuery = useEntitySuggestion(suggestionId);
  const lastShown = useRef<PopulatedEntitySuggestion | undefined>(undefined);
  // A list refetch can drop the row before the detail has data (e.g. mid-approve);
  // the body and its nested dialogs must stay mounted for the dialog's lifetime.
  const shown = suggestionQuery.data ?? listSuggestion ?? lastShown.current;
  lastShown.current = shown;

  return (
    <>
      <Dialog open onOpenChange={(open) => !open && onClose()}>
        <DialogShell size="lg">
          {shown ? (
            <ReviewModalBody
              suggestion={shown}
              onClose={onClose}
              onReject={() => setRejectOpen(true)}
              onPreviewRecording={onPreviewRecording}
            />
          ) : (
            <div className="flex flex-1 min-h-0 flex-col">
              <DialogShellHeader title={t('myTasks.review')} onClose={onClose} />
              <DialogShellBody>
                <DialogLoadStateBody
                  loadState={readDialogLoadState([suggestionQuery])}
                  notFoundKey="errors.entitySuggestion.notFound"
                  skeleton={
                    <>
                      <Skeleton className="h-20" />
                      <Skeleton className="h-32" />
                      <Skeleton className="h-24" />
                    </>
                  }
                />
              </DialogShellBody>
            </div>
          )}
        </DialogShell>
      </Dialog>
      <RejectConfirmDialog
        open={rejectOpen}
        suggestionId={suggestionId}
        onCancel={() => setRejectOpen(false)}
        onSuccess={() => {
          setRejectOpen(false);
          onClose();
        }}
      />
    </>
  );
}

function ReviewModalBody({
  suggestion,
  onClose,
  onReject,
  onPreviewRecording
}: {
  suggestion: PopulatedEntitySuggestion;
  onClose: () => void;
  onReject: () => void;
  onPreviewRecording: (recordingId: string) => void;
}) {
  // Start-event suggestions have `sourceProcessId` instead of
  // `sourceRecordingId`. Recording-preview is meaningless for those, so we
  // skip the click when the source isn't a recording.
  const previewRecording = () => {
    if (suggestion.sourceRecordingId) {
      onPreviewRecording(suggestion.sourceRecordingId);
    }
  };
  return (
    <SuggestionReviewBody
      suggestion={suggestion}
      onClose={onClose}
      onReject={onReject}
      onPreviewRecording={previewRecording}
    />
  );
}
